import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { useTranslate } from 'src/locales';

import Iconify from 'src/components/iconify';

import { SearchExplanation } from 'src/services/candidates/candidates.types';

// ----------------------------------------------------------------------

/**
 * What the AI search understood, approximated, and could not do (JT1-1618).
 *
 * Without this, an empty result set is indistinguishable from a broken feature. During JT1-1617's
 * testing every "no results" needed a database query to explain — the term was searched as free
 * text because nobody had that skill, or the constraint had no field at all. The backend already
 * knew all of that and had nowhere to say it.
 *
 * Three sections, in descending order of how much the recruiter can act on them:
 *   understood — what is actually filtering, so they can see and edit it
 *   fell back  — searched, but loosely, and why
 *   unmapped   — not searched at all, so they know to look for it another way
 */

type Props = {
  explanation: SearchExplanation | null;
};

export default function NLSearchExplanation({ explanation }: Props) {
  const { t } = useTranslate();

  if (!explanation) return null;

  const { summary, understood, fell_back, unmapped, fell_back_entirely } = explanation;

  return (
    <Stack spacing={1.5}>
      {!!summary && (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {summary}
        </Typography>
      )}

      {/* the whole query was searched as free text — say so plainly, because the results will look
          arbitrary otherwise */}
      {fell_back_entirely && (
        <Alert severity="warning" icon={<Iconify icon="solar:magnifer-bold-duotone" />}>
          {t('components.advanced_search.ai_explanation_all_free_text')}
        </Alert>
      )}

      {understood.length > 0 && (
        <Box>
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75 }}>
            <Iconify
              icon="eva:checkmark-circle-2-fill"
              width={16}
              sx={{ color: 'success.main', flexShrink: 0 }}
            />
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              {t('components.advanced_search.ai_explanation_searching_for')}
            </Typography>
          </Stack>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {understood.map((item, index) => (
              <Chip
                // field is not unique — one search can carry two skills groups
                key={`${item.field}-${index}`}
                size="small"
                variant="soft"
                color="success"
                label={
                  <Typography variant="caption" component="span">
                    <strong>{item.label}</strong>
                    {item.detail ? `: ${item.detail}` : ''}
                  </Typography>
                }
              />
            ))}
          </Box>
        </Box>
      )}

      {fell_back.length > 0 && (
        <Alert severity="info" icon={<Iconify icon="solar:document-text-bold-duotone" />}>
          <Typography variant="caption" component="div" sx={{ fontWeight: 600, mb: 0.25 }}>
            {t('components.advanced_search.ai_explanation_free_text')}
          </Typography>
          <Stack spacing={0.25}>
            {fell_back.map((item, index) => (
              <Typography key={`${item.text}-${index}`} variant="caption" component="div">
                <strong>{item.text}</strong> — {item.reason}
              </Typography>
            ))}
          </Stack>
        </Alert>
      )}

      {unmapped.length > 0 && (
        <Alert severity="warning" icon={<Iconify icon="solar:danger-triangle-bold-duotone" />}>
          <Typography variant="caption" component="div" sx={{ fontWeight: 600, mb: 0.25 }}>
            {t('components.advanced_search.ai_explanation_not_searched')}
          </Typography>
          <Stack spacing={0.25}>
            {unmapped.map((item, index) => (
              <Typography key={`${item.text}-${index}`} variant="caption" component="div">
                <strong>{item.text}</strong> — {item.reason}
              </Typography>
            ))}
          </Stack>
        </Alert>
      )}
    </Stack>
  );
}
